import { safeImageUri } from '@/lib/safe-image-uri';
import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '사진 등록 결과를 확인할 수 없습니다.',
  });
const positive = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) > 0;
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export interface MediaWriteReceipt {
  commandId: string;
  targetId: string;
  targetKind: 'CONTENT_COLLECTION' | 'CONTENT_ASSIGNMENT';
  collectionId: string | null;
  itemId: string | null;
  resultRevision: number;
  collectionRevision: number | null;
  httpStatus: 200 | 201;
}

function parseReceipt(value: unknown, commandId: string, collectionId?: string): MediaWriteReceipt {
  if (
    !record(value) ||
    Object.keys(value).sort().join(',') !==
      [
        'command_id',
        'target_id',
        'target_kind',
        'collection_id',
        'item_id',
        'result_revision',
        'collection_revision',
        'http_status',
      ]
        .sort()
        .join(',') ||
    value.command_id !== commandId ||
    !isUuid(value.target_id) ||
    (value.target_kind !== 'CONTENT_COLLECTION' && value.target_kind !== 'CONTENT_ASSIGNMENT') ||
    !(value.collection_id === null || isUuid(value.collection_id)) ||
    !(value.item_id === null || isUuid(value.item_id)) ||
    !positive(value.result_revision) ||
    !(value.collection_revision === null || positive(value.collection_revision)) ||
    (value.http_status !== 200 && value.http_status !== 201) ||
    (collectionId
      ? value.collection_id !== collectionId ||
        value.item_id === null ||
        value.target_id !== collectionId ||
        value.target_kind !== 'CONTENT_COLLECTION'
      : value.collection_id !== value.target_id ||
        value.item_id !== null ||
        value.target_kind !== 'CONTENT_COLLECTION')
  )
    throw invalid();
  return {
    commandId,
    targetId: value.target_id,
    targetKind: value.target_kind,
    collectionId: value.collection_id,
    itemId: value.item_id,
    resultRevision: value.result_revision,
    collectionRevision: value.collection_revision,
    httpStatus: value.http_status,
  };
}

interface MediaCollectionBase {
  command_id: string;
  owner_property_id: string;
  purpose: 'GALLERY';
  language: 'ko';
  name: string | null;
  sort_order: number;
  category_key: null;
}

export type CreateMediaCollection = MediaCollectionBase &
  (
    | {
        owner_scope_kind: 'PROPERTY';
        owner_room_type_id: null;
        owner_room_id: null;
        owner_unit_id: null;
      }
    | {
        owner_scope_kind: 'UNIT';
        owner_room_type_id: null;
        owner_room_id: string;
        owner_unit_id: string;
      }
    | {
        owner_scope_kind: 'ROOM_TYPE';
        owner_room_type_id: string;
        owner_room_id: null;
        owner_unit_id: null;
      }
  );
export type CreateTypeMediaCollection = Extract<
  CreateMediaCollection,
  { owner_scope_kind: 'ROOM_TYPE' }
>;
export const createTypeMediaCollection = createMediaCollection;

export async function createMediaCollection(client: ServiceClient, body: CreateMediaCollection) {
  const input = body as unknown as Record<string, unknown>;
  if (
    !isUuid(input.command_id) ||
    !isUuid(input.owner_property_id) ||
    !(input.owner_scope_kind === 'PROPERTY'
      ? input.owner_room_type_id === null &&
        input.owner_room_id === null &&
        input.owner_unit_id === null
      : input.owner_scope_kind === 'UNIT'
        ? input.owner_room_type_id === null &&
          isUuid(input.owner_room_id) &&
          isUuid(input.owner_unit_id)
        : input.owner_scope_kind === 'ROOM_TYPE' &&
          isUuid(input.owner_room_type_id) &&
          input.owner_room_id === null &&
          input.owner_unit_id === null) ||
    input.purpose !== 'GALLERY' ||
    input.language !== 'ko' ||
    input.name !== null ||
    input.category_key !== null ||
    !Number.isSafeInteger(input.sort_order) ||
    typeof input.sort_order !== 'number' ||
    input.sort_order < 0 ||
    input.sort_order > 2147483647
  )
    throw invalid();
  return parseReceipt(
    await client.post<unknown>('admin/media-collections', {
      json: {
        command_id: body.command_id,
        owner_scope_kind: body.owner_scope_kind,
        owner_property_id: body.owner_property_id,
        owner_room_type_id: body.owner_room_type_id,
        owner_room_id: body.owner_room_id,
        owner_unit_id: body.owner_unit_id,
        purpose: body.purpose,
        language: body.language,
        name: body.name,
        sort_order: body.sort_order,
        category_key: body.category_key,
      },
    }),
    body.command_id,
  );
}

export interface CreateMediaUrl {
  command_id: string;
  expected_collection_revision: number;
  title: string | null;
  primary_uri: string;
  preview_uri: string | null;
}

export async function createMediaUrl(
  client: ServiceClient,
  collectionId: string,
  body: CreateMediaUrl,
) {
  if (
    !isUuid(collectionId) ||
    !isUuid(body.command_id) ||
    !positive(body.expected_collection_revision) ||
    !safeImageUri(body.primary_uri) ||
    (body.title !== null && typeof body.title !== 'string') ||
    (body.preview_uri !== null && !safeImageUri(body.preview_uri))
  )
    throw invalid();
  return parseReceipt(
    await client.post<unknown>(`admin/media-collections/${collectionId}/items`, {
      json: {
        command_id: body.command_id,
        expected_collection_revision: body.expected_collection_revision,
        title: body.title,
        primary_uri: body.primary_uri,
        preview_uri: body.preview_uri,
      },
    }),
    body.command_id,
    collectionId,
  );
}

export interface UploadManagedImage {
  commandId: string;
  collectionId: string;
  expectedCollectionRevision: number;
  file: File;
}

export async function uploadManagedImage(client: ServiceClient, attempt: UploadManagedImage) {
  const { commandId, collectionId, expectedCollectionRevision, file } = attempt;
  if (
    !isUuid(commandId) ||
    !isUuid(collectionId) ||
    !positive(expectedCollectionRevision) ||
    !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
    file.size < 1 ||
    file.size > 10_000_000
  )
    throw invalid();
  return parseReceipt(
    await client.post<unknown>(`admin/media-collections/${collectionId}/managed-items`, {
      params: { command_id: commandId, expected_collection_revision: expectedCollectionRevision },
      body: file,
      headers: { 'Content-Type': file.type },
    }),
    commandId,
    collectionId,
  );
}
